import { useTellescopeMutation } from "@tellescope/tanstack-query";
import { useCallback, useMemo } from "react";
import { normalizeResponseForSubmission } from "../../utils";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface UseSaveResponseArgs {
  formId: string;
  enduserId: string;
  responseId: string | undefined;
  parentResponseId: string | undefined;
  rootResponseId: string | undefined;
  formTitle: string | undefined;
  response: { id: string } | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
}

export const useSaveResponse = ({
  formId,
  enduserId,
  responseId,
  parentResponseId,
  rootResponseId,
  formTitle,
  response,
  currentResponses,
}: UseSaveResponseArgs) => {
  const prepareFormResponseMutation = useTellescopeMutation({
    model: "form_responses",
    method: "prepare_form_response",
    autoCacheUpdateStrategy: "update",
  });

  const preparedResponse = useMemo(() => {
    const prepared = prepareFormResponseMutation.data?.response;
    return prepared?.formId === formId ? prepared : undefined;
  }, [formId, prepareFormResponseMutation.data?.response]);

  const updateResponseMutation = useTellescopeMutation({
    model: "form_responses",
    method: "updateOne",
    autoCacheUpdateStrategy: "update",
  });

  const submitFormMutation = useTellescopeMutation({
    model: "form_responses",
    method: "submit_form_response",
    autoCacheUpdateStrategy: "update",
  });

  const saveFieldResponseMutation = useTellescopeMutation({
    model: "form_responses",
    method: "save_field_response",
  });

  const saveResponse = useCallback(async () => {
    let result: { id: string } | undefined = response ?? preparedResponse;

    if (!responseId && !result) {
      const res = await prepareFormResponseMutation.mutateAsync({
        formId,
        enduserId,
        parentResponseId,
        rootResponseId,
        title: formTitle,
      });
      result = res.response;
    }

    if (!result) return;

    await updateResponseMutation.mutateAsync({
      id: result.id,
      responses: Object.values(currentResponses).map(normalizeResponseForSubmission),
      _options: {
        replaceObjectFields: true,
      },
    });
  }, [
    currentResponses,
    enduserId,
    formId,
    formTitle,
    parentResponseId,
    preparedResponse,
    prepareFormResponseMutation,
    response,
    responseId,
    rootResponseId,
    updateResponseMutation,
  ]);

  return {
    prepareFormResponseMutation,
    preparedResponse,
    updateResponseMutation,
    submitFormMutation,
    saveFieldResponseMutation,
    saveResponse,
  };
};
